import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Settings2, Snowflake } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { refreshBuildingModule } from '../../utils/refreshAfterSave';
import BuildingPageHeading from '../../components/BuildingPageHeading';
import SectionCard, { sectionTableHeadClass } from '../../components/SectionCard';
import InspectionAssetChecklist from '../../components/InspectionAssetChecklist';
import InspectionFloorPlan from '../../components/InspectionFloorPlan';
import InspectionQuestionEditor from '../../components/InspectionQuestionEditor';
import CalibrationGraph from './CalibrationGraph';

const ASSET_TYPES = [
  { value: 'CHILLER', label: 'Chiller' },
  { value: 'FREEZER', label: 'Freezer' },
  { value: 'COLD_ROOM', label: 'Cold room' },
  { value: 'OTHER', label: 'Other' },
];

const SENSOR_TYPES = [
  { value: 'NTC', label: 'NTC', limit: '1.0' },
  { value: 'PTC', label: 'PTC', limit: '1.0' },
  { value: 'PT100', label: 'PT100', limit: '0.5' },
  { value: 'PT1000', label: 'PT1000', limit: '0.5' },
];

function sensorLimit(type) {
  const row = SENSOR_TYPES.find((item) => item.value === String(type || '').toUpperCase());
  return row ? row.limit : '1.0';
}

/** Sensor code: door → DS1… · monitoring → MS1… */
function sensorCode(role, seq) {
  const n = Math.max(1, Number(seq) || 1);
  return role === 'door' ? `DS${n}` : `MS${n}`;
}

/**
 * Short chiller tag: Chiller 1 → C1, Freezer → F, Coolroom 2 → CR2.
 */
function chillerShortTag(name) {
  const s = String(name || '').trim();
  if (!s) return '';
  if (/^[A-Za-z]{1,3}\d{0,3}$/.test(s)) return s.toUpperCase();
  const chiller = s.match(/\bchillers?\s*(\d+)\b/i) || s.match(/\bc\s*(\d+)\b/i);
  if (chiller) return `C${chiller[1]}`;
  const freezer = s.match(/\bfreezers?\s*(\d*)\b/i);
  if (freezer) return freezer[1] ? `F${freezer[1]}` : 'F';
  const cool = s.match(/\bcool\s*-?\s*rooms?\s*(\d*)\b/i) || s.match(/\bcoolrooms?\s*(\d*)\b/i);
  if (cool) return cool[1] ? `CR${cool[1]}` : 'CR';
  const num = (s.match(/(\d+)\s*$/) || [])[1] || '';
  const words = s
    .replace(/(\d+)\s*$/, '')
    .trim()
    .split(/[\s/_-]+/)
    .filter(Boolean);
  const letters = words
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 3);
  return `${letters}${num}` || s.slice(0, 3).toUpperCase();
}

/** Compound code when chiller context is needed: C1-DS1, F-MS1. */
function reportSensorName(chillerName, code) {
  const tag = chillerShortTag(chillerName);
  const sn = String(code || '').trim();
  if (!tag) return sn;
  if (!sn) return tag;
  return `${tag}-${sn}`;
}

/** Test title: "Chiller 1 door sensor temperature" / "Chiller 1 monitoring sensor 1 temperature". */
function sensorTestTitle(chillerName, role, seq) {
  const head = String(chillerName || '').trim() || 'Chiller';
  if (role === 'door') {
    const n = Math.max(1, Number(seq) || 1);
    return n > 1
      ? `${head} door sensor ${n} temperature`
      : `${head} door sensor temperature`;
  }
  return `${head} monitoring sensor ${Math.max(1, Number(seq) || 1)} temperature`;
}

function sensorRole(row, index) {
  const explicit = String(row?.role || row?.kind || '').toLowerCase();
  if (explicit === 'door' || explicit === 'controller') return 'door';
  if (explicit === 'monitoring' || explicit === 'logger' || explicit === 'monitor') return 'monitoring';
  const id = String(row?.id || '').toLowerCase();
  if (id === 'cal-door' || id.startsWith('cal-door')) return 'door';
  if (id === 'cal-logger' || id.startsWith('cal-mon') || id.startsWith('cal-logger')) return 'monitoring';
  // Legacy rows: first card = door sensor; everything else = monitoring sensor.
  return index === 0 ? 'door' : 'monitoring';
}

function defaultCalibrationSensors(asset) {
  const saved = Array.isArray(asset?.calibration_sensors) ? asset.calibration_sensors : null;
  const source = saved && saved.length
    ? saved
    : [
        {
          id: 'cal-door',
          role: 'door',
          label: 'Door sensor',
          model: asset?.door_controller_model || '',
          sensor: asset?.door_sensor_type || 'NTC',
          pass_c: asset?.door_pass_c || '',
        },
        {
          id: 'cal-logger',
          role: 'monitoring',
          label: 'Monitoring sensor 1',
          model: asset?.logger_model || '',
          sensor: asset?.logger_sensor_type || 'PT100',
          pass_c: asset?.logger_pass_c || '',
        },
      ];
  let doorSeq = 0;
  let monitorSeq = 0;
  return source.map((row, index) => {
    const role = sensorRole(row, index);
    if (role === 'door') doorSeq += 1;
    else monitorSeq += 1;
    const seq = role === 'door' ? doorSeq : monitorSeq;
    const code = sensorCode(role, seq);
    const sensor = String(
      row.sensor || row.sensor_type || (role === 'monitoring' ? 'PT100' : 'NTC')
    ).toUpperCase();
    const id =
      role === 'door'
        ? String(row.id || 'cal-door')
        : index === 1 && (!row.id || row.id === 'cal-logger')
          ? 'cal-logger'
          : String(row.id || `cal-mon-${monitorSeq}`);
    const fallbackLabel =
      role === 'door' ? 'Door sensor' : `Monitoring sensor ${monitorSeq}`;
    let label = String(row.label || row.display || '').trim();
    if (label === 'Door controller' || label === 'Door Controller') label = 'Door sensor';
    if (/^Monitoring(\s+logger)?(\s+\d+)?$/i.test(label)) {
      label = `Monitoring sensor ${monitorSeq}`;
    }
    const out = {
      id,
      role,
      code,
      seq,
      label: label || fallbackLabel,
      model: String(row.model || ''),
      sensor,
      pass_c: String(row.pass_c || row.limit || sensorLimit(sensor)),
      // Per-sensor in-use flag (door or monitoring). Default on.
      enabled: !(row.enabled === false || row.enabled === 0 || row.enabled === '0'),
    };
    if (row.pin_x != null && row.pin_y != null) {
      out.pin_x = Number(row.pin_x);
      out.pin_y = Number(row.pin_y);
      out.level_id = row.level_id || null;
    }
    return out;
  });
}

function sensorsOfAsset(asset) {
  return defaultCalibrationSensors(asset).map((row) => ({
    ...row,
    // letter kept for older UI call sites — same as DS1/MS1 code.
    letter: row.code,
  }));
}

function newMonitoringSensor(existingRows) {
  const rows = Array.isArray(existingRows) ? existingRows : [];
  const monitorCount = rows.filter((row) => sensorRole(row, 1) === 'monitoring').length;
  const next = monitorCount + 1;
  return {
    id: `cal-mon-${Date.now().toString(36)}`,
    role: 'monitoring',
    label: `Monitoring sensor ${next}`,
    model: '',
    sensor: 'PT100',
    pass_c: sensorLimit('PT100'),
    enabled: true,
  };
}

const EMPTY_ASSET = {
  name: '',
  asset_tag: '',
  asset_type: 'CHILLER',
  electrical_phase: '',
  manufacturer: '',
  model: '',
  serial_number: '',
  location_label: '',
  next_service_due: '',
  next_calibration_due: '',
  last_service_at: '',
  last_calibration_at: '',
  schedule_group_id: '',
  door_controller_model: '',
  door_sensor_type: 'NTC',
  door_pass_c: '1.0',
  logger_model: '',
  logger_sensor_type: 'PT100',
  logger_pass_c: '0.5',
  no_monitoring_equipment: false,
};

function formatDate(v) {
  if (!v) return '—';
  const s = String(v);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : s.slice(0, 10);
}

function formatDisplayDate(v) {
  const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
}

function dateInput(v) {
  if (!v) return '';
  const m = String(v).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}

function isActiveAsset(asset) {
  return String(asset?.status || 'ACTIVE').toUpperCase() === 'ACTIVE';
}

function assetToForm(asset) {
  return {
    name: asset.name || '',
    asset_tag: asset.asset_tag || '',
    asset_type: asset.asset_type || 'CHILLER',
    electrical_phase: asset.electrical_phase ? String(asset.electrical_phase) : '',
    manufacturer: asset.manufacturer || '',
    model: asset.model || '',
    serial_number: asset.serial_number || '',
    location_label: asset.location_label || '',
    next_service_due: dateInput(asset.next_service_due),
    next_calibration_due: dateInput(asset.next_calibration_due),
    last_service_at: dateInput(asset.last_service_at),
    last_calibration_at: dateInput(asset.last_calibration_at),
    schedule_group_id: asset.schedule_group_id != null ? String(asset.schedule_group_id) : '',
    door_controller_model: asset.door_controller_model || '',
    door_sensor_type: asset.door_sensor_type || 'NTC',
    door_pass_c: asset.door_pass_c || sensorLimit(asset.door_sensor_type || 'NTC'),
    logger_model: asset.logger_model || '',
    logger_sensor_type: asset.logger_sensor_type || 'PT100',
    logger_pass_c: asset.logger_pass_c || sensorLimit(asset.logger_sensor_type || 'PT100'),
    no_monitoring_equipment: asset.no_monitoring_equipment === true || asset.no_monitoring_equipment === 1,
    calibration_sensors: defaultCalibrationSensors(asset),
  };
}

const DEFAULT_PLAN = {
  Q1: { packs: ['A', 'B'], calibration: true },
  Q2: { packs: ['A'], calibration: false },
  Q3: { packs: ['A', 'C'], calibration: true },
  Q4: { packs: ['A'], calibration: false },
};

const INTERVAL_OPTIONS = [
  { id: 'EVERY_VISIT', label: 'Every visit' },
  { id: 'THREE_MONTHS', label: 'Every 3 months' },
  { id: 'SIX_MONTHS', label: 'Every 6 months' },
  { id: 'YEARLY', label: 'Yearly' },
  { id: 'YEARLY_OFFSET', label: 'Yearly, 6 months later' },
  { id: 'OFF', label: 'Not used' },
];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function addCycleMonth(start, delta) {
  return ((Number(start) - 1 + delta) % 12 + 12) % 12;
}

/** Frequency visit months as 1–12. */
function frequencyVisitMonths(frequency, startMonth) {
  const start = Math.min(12, Math.max(1, Number(startMonth) || 1));
  const visitSteps = frequency === 'YEARLY' ? [0] : frequency === 'HALF_YEARLY' ? [0, 6] : [0, 3, 6, 9];
  return visitSteps.map((step) => addCycleMonth(start, step) + 1);
}

function normalizeMonthList(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  raw.forEach((value) => {
    const month = Math.min(12, Math.max(1, Number(value) || 0));
    if (!month || seen.has(month)) return;
    seen.add(month);
    out.push(month);
  });
  return out.sort((a, b) => a - b);
}

/**
 * Display months for a pack interval.
 * Returns 0-based indices for MONTH_NAMES.
 * EVERY_VISIT uses customMonths (1–12) when set; otherwise frequency visit months.
 */
function monthsForInterval(interval, frequency, startMonth, customMonths) {
  if (interval === 'OFF') return [];
  if (interval === 'EVERY_VISIT') {
    const custom = normalizeMonthList(customMonths);
    if (custom.length) return custom.map((m) => m - 1);
    return frequencyVisitMonths(frequency, startMonth).map((m) => m - 1);
  }
  const start = Math.min(12, Math.max(1, Number(startMonth) || 1));
  const visitSteps = frequency === 'YEARLY' ? [0] : frequency === 'HALF_YEARLY' ? [0, 6] : [0, 3, 6, 9];
  const visits = visitSteps.map((step) => addCycleMonth(start, step));
  const steps =
    interval === 'YEARLY'
      ? [0]
      : interval === 'YEARLY_OFFSET'
        ? [6]
        : interval === 'SIX_MONTHS'
          ? [0, 6]
          : [0, 3, 6, 9];
  const due = new Set(steps.map((step) => addCycleMonth(start, step)));
  return visits.filter((monthIndex) => due.has(monthIndex));
}

function intervalsFromPlan(plan, frequency) {
  const source = plan && typeof plan === 'object' ? plan : DEFAULT_PLAN;
  if (source.intervals && typeof source.intervals === 'object') {
    const out = {};
    Object.keys(source.intervals).forEach((code) => {
      const key = String(code || '').toUpperCase();
      const value = String(source.intervals[code] || '').toUpperCase();
      out[key] = INTERVAL_OPTIONS.some((option) => option.id === value) ? value : 'EVERY_VISIT';
    });
    return out;
  }
  const slots =
    frequency === 'YEARLY' ? ['Y1'] : frequency === 'HALF_YEARLY' ? ['H1', 'H2'] : ['Q1', 'Q2', 'Q3', 'Q4'];
  const codes = ['A', 'B', 'C'];
  const out = {};
  codes.forEach((code) => {
    const on = slots.filter((slot) =>
      (source[slot]?.packs || []).map((pack) => String(pack).toUpperCase()).includes(code)
    );
    if (!on.length) out[code] = 'OFF';
    else if (on.length === slots.length) out[code] = 'EVERY_VISIT';
    else if (on.length === 1 && (on[0] === 'Q1' || on[0] === 'H1' || on[0] === 'Y1')) out[code] = 'YEARLY';
    else if (on.length === 1 && (on[0] === 'Q3' || on[0] === 'H2')) out[code] = 'YEARLY_OFFSET';
    else if (on.includes('Q1') && on.includes('Q3')) out[code] = 'SIX_MONTHS';
    else out[code] = 'EVERY_VISIT';
  });
  return out;
}

/** Custom months map for EVERY_VISIT packs (1–12). */
function intervalMonthsFromPlan(plan, intervals, frequency, startMonth) {
  const source = plan && typeof plan === 'object' ? plan : {};
  const out = {};
  const stored =
    source.interval_months && typeof source.interval_months === 'object' ? source.interval_months : {};
  Object.keys(intervals || {}).forEach((code) => {
    if (String(intervals[code]).toUpperCase() !== 'EVERY_VISIT') return;
    const fromStore = normalizeMonthList(stored[code]);
    out[code] = fromStore.length ? fromStore : frequencyVisitMonths(frequency, startMonth);
  });
  return out;
}

function buildPackagePlan(intervals, intervalMonths) {
  const months = {};
  Object.keys(intervals || {}).forEach((code) => {
    if (String(intervals[code]).toUpperCase() !== 'EVERY_VISIT') return;
    const list = normalizeMonthList(intervalMonths?.[code]);
    if (list.length) months[code] = list;
  });
  return {
    intervals,
    ...(Object.keys(months).length ? { interval_months: months } : {}),
  };
}

export default function BuildingChiller({ moduleCode = 'CHILLER' }) {
  const { id: buildingId } = useParams();
  const queryClient = useQueryClient();
  const apiBase = `/inspection-modules/${moduleCode}`;
  const [showAdd, setShowAdd] = useState(false);
  const [checklistAsset, setChecklistAsset] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [packCode, setPackCode] = useState('D');
  const [form, setForm] = useState({ ...EMPTY_ASSET });
  const [settingsForm, setSettingsForm] = useState(() => {
    const pack_intervals = intervalsFromPlan(DEFAULT_PLAN, 'QUARTERLY');
    return {
      frequency: 'QUARTERLY',
      cycle_start_month: 1,
      cycle_start_date: '',
      pack_intervals,
      pack_interval_months: intervalMonthsFromPlan(DEFAULT_PLAN, pack_intervals, 'QUARTERLY', 1),
      notes: '',
    };
  });
  const [orderKind, setOrderKind] = useState('INSPECTION');
  const [graphOpen, setGraphOpen] = useState(false);
  const [meterForm, setMeterForm] = useState({ make: '', model: '', serial: '' });
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupNameDraft, setGroupNameDraft] = useState('');
  const [groupLevelDraft, setGroupLevelDraft] = useState('');
  const [acceptDueDraft, setAcceptDueDraft] = useState({ service: '', calibration: '' });
  const orderParams = orderKind === 'CALIBRATION' ? { order: 'CALIBRATION' } : undefined;

  const { data: deviceTypes = [] } = useQuery({
    queryKey: ['inspection-modules', 'device-types'],
    queryFn: async () => (await api.get('/inspection-modules/device-types')).data?.data || [],
    staleTime: 60_000,
  });
  const deviceRow = deviceTypes.find((row) => String(row.module_code).toUpperCase() === moduleCode);
  const deviceLabel = String(deviceRow?.label || (moduleCode === 'CHILLER' ? 'Chiller' : moduleCode))
    .split('/')[0]
    .trim();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['building', buildingId, moduleCode, orderKind, 'assets'],
    queryFn: async () =>
      (
        await api.get(`${apiBase}/buildings/${buildingId}/assets`, {
          params: orderParams,
        })
      ).data?.data,
    enabled: !!buildingId,
    retry: false,
  });

  const { data: settings } = useQuery({
    queryKey: ['building', buildingId, moduleCode, orderKind, 'settings'],
    queryFn: async () =>
      (await api.get(`${apiBase}/buildings/${buildingId}/settings`, { params: orderParams })).data?.data,
    enabled: !!buildingId && !error?.response?.status,
    retry: false,
  });

  // API/MySQL may return 0/1. `0 !== false` is true in JS — treat 0/'0' as unlinked.
  const linkFlag = data?.assets_linked ?? settings?.assets_linked;
  const assetsLinked = !(linkFlag === false || linkFlag === 0 || linkFlag === '0');

  const { data: visits = [], isLoading: visitsLoading } = useQuery({
    queryKey: ['building', buildingId, moduleCode, orderKind, 'visits'],
    queryFn: async () =>
      (await api.get(`${apiBase}/buildings/${buildingId}/visits`, { params: orderParams })).data?.data || [],
    enabled: !!buildingId && !error?.response?.status,
    retry: false,
  });

  const { data: workers = [] } = useQuery({
    queryKey: ['workers', 'active-for-plant'],
    queryFn: async () =>
      (await api.get('/workers', { params: { limit: 500, status: 'active' } })).data?.data || [],
    staleTime: 60_000,
  });

  const [assignDraft, setAssignDraft] = useState({});

  useEffect(() => {
    const next = {};
    (Array.isArray(visits) ? visits : []).forEach((v) => {
      if (String(v.status || '').toUpperCase() !== 'OPEN') return;
      next[v.id] = (v.assignees || []).map((a) => String(a.worker_id));
    });
    setAssignDraft(next);
  }, [visits]);

  const assignVisitMutation = useMutation({
    mutationFn: async ({ visitId, workerIds }) =>
      (
        await api.put(`${apiBase}/buildings/${buildingId}/visits/${visitId}/assignees`, {
          worker_ids: workerIds,
        })
      ).data?.data,
    onSuccess: async () => {
      await afterBuildingSave('Workers assigned — they see this visit on the phone Inspection list');
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to assign workers'),
  });

  const { data: scheduleGroups = [] } = useQuery({
    queryKey: ['building', buildingId, moduleCode, 'schedule-groups'],
    queryFn: async () =>
      (await api.get(`${apiBase}/buildings/${buildingId}/schedule-groups`)).data?.data || [],
    enabled: !!buildingId && !error?.response?.status,
    retry: false,
  });

  const selectedGroup =
    scheduleGroups.find((g) => Number(g.id) === Number(selectedGroupId)) ||
    scheduleGroups.find((g) => g.is_default) ||
    scheduleGroups[0] ||
    null;

  useEffect(() => {
    if (!scheduleGroups.length) return;
    if (selectedGroupId && scheduleGroups.some((g) => Number(g.id) === Number(selectedGroupId))) return;
    const def = scheduleGroups.find((g) => g.is_default) || scheduleGroups[0];
    if (def) setSelectedGroupId(def.id);
  }, [scheduleGroups, selectedGroupId]);

  useEffect(() => {
    if (selectedGroup) {
      const frequency = selectedGroup.frequency || 'QUARTERLY';
      const cycle_start_month =
        selectedGroup.cycle_start_month ||
        (selectedGroup.cycle_start_date
          ? Number(String(selectedGroup.cycle_start_date).slice(5, 7))
          : 1);
      const pack_intervals = intervalsFromPlan(selectedGroup.package_plan, frequency);
      setSettingsForm({
        frequency,
        cycle_start_month,
        cycle_start_date: dateInput(selectedGroup.cycle_start_date),
        pack_intervals,
        pack_interval_months: intervalMonthsFromPlan(
          selectedGroup.package_plan,
          pack_intervals,
          frequency,
          cycle_start_month
        ),
        notes: selectedGroup.notes || '',
      });
      setGroupNameDraft(selectedGroup.name || '');
      setGroupLevelDraft(selectedGroup.level_id ? String(selectedGroup.level_id) : '');
      const members = (Array.isArray(data?.assets) ? data.assets : []).filter(
        (a) => Number(a.schedule_group_id) === Number(selectedGroup.id)
      );
      const sample = members[0];
      setAcceptDueDraft({
        service: dateInput(
          selectedGroup.accepted_service_due ||
            sample?.proposed_service_due ||
            sample?.next_service_due
        ),
        calibration: dateInput(
          selectedGroup.accepted_calibration_due ||
            sample?.proposed_calibration_due ||
            sample?.next_calibration_due
        ),
      });
      return;
    }
    if (!settings) return;
    const frequency = settings.frequency || 'QUARTERLY';
    const cycle_start_month = settings.cycle_start_month || 1;
    const pack_intervals = intervalsFromPlan(settings.package_plan, frequency);
    setSettingsForm({
      frequency,
      cycle_start_month,
      cycle_start_date: '',
      pack_intervals,
      pack_interval_months: intervalMonthsFromPlan(
        settings.package_plan,
        pack_intervals,
        frequency,
        cycle_start_month
      ),
      notes: settings.notes || '',
    });
  }, [selectedGroup, settings, data?.assets]);

  /** Save succeeded → re-fetch this building module (settings, visits, assets, groups). */
  async function afterBuildingSave(message) {
    if (message) toast.success(message);
    await refreshBuildingModule(queryClient, buildingId, moduleCode);
    await queryClient.invalidateQueries({ queryKey: ['chiller', 'dashboard'] });
  }

  const saveAsset = useMutation({
    mutationFn: async (payload) => {
      // Door and monitoring sensors are independent — enable either or both.
      let sensors = defaultCalibrationSensors({ calibration_sensors: payload.calibration_sensors });
      if (payload.no_monitoring_equipment) {
        sensors = sensors.map((row) =>
          row.role === 'monitoring' ? { ...row, enabled: false } : row
        );
      }
      const door = sensors.find((row) => row.role === 'door') || sensors[0] || {};
      const logger = sensors.find((row) => row.role === 'monitoring') || {};
      const body = {
        ...payload,
        electrical_phase: payload.electrical_phase ? Number(payload.electrical_phase) : null,
        calibration_sensors: sensors,
        door_controller_model: door.model || '',
        door_sensor_type: door.sensor || 'NTC',
        door_pass_c: door.pass_c || sensorLimit(door.sensor),
        // Legacy single-logger fields keep the first monitoring probe for older clients.
        logger_model: logger.model || '',
        logger_sensor_type: logger.sensor || 'PT100',
        logger_pass_c: logger.pass_c || sensorLimit(logger.sensor),
      };
      delete body.control_source;
      delete body.next_service_due;
      delete body.next_calibration_due;
      delete body.last_service_at;
      delete body.last_calibration_at;
      if (!editingId && selectedGroup?.id) {
        body.schedule_group_id = selectedGroup.id;
      } else if (body.schedule_group_id === '') {
        body.schedule_group_id = null;
      }
      body.order_kind = orderKind;
      if (editingId) {
        return (await api.patch(`${apiBase}/buildings/${buildingId}/assets/${editingId}`, body)).data?.data;
      }
      return (await api.post(`${apiBase}/buildings/${buildingId}/assets`, body)).data?.data;
    },
    onSuccess: async () => {
      setShowAdd(false);
      setEditingId(null);
      setForm({ ...EMPTY_ASSET, calibration_sensors: defaultCalibrationSensors(null) });
      await afterBuildingSave(editingId ? 'Asset updated' : 'Asset added');
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to save asset'),
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }) =>
      (await api.patch(`${apiBase}/buildings/${buildingId}/assets/${id}`, { status })).data?.data,
    onSuccess: async (_data, vars) => {
      await afterBuildingSave(
        vars.status === 'ACTIVE' ? 'Asset enabled' : vars.status === 'PURGED' ? 'Asset removed' : 'Asset disabled'
      );
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to update asset'),
  });

  const assetsLinkMutation = useMutation({
    mutationFn: async (linked) =>
      (await api.put(`${apiBase}/buildings/${buildingId}/assets-link`, { assets_linked: linked })).data
        ?.data,
    onSuccess: async (res) => {
      await afterBuildingSave(
        res?.assets_linked
          ? 'Chiller and Calibration units linked again'
          : 'Chiller and Calibration units unlinked — lists are separate'
      );
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to update unit link'),
  });

  useEffect(() => {
    if (orderKind !== 'CALIBRATION') return;
    const kit = settings?.calibration_kit || {};
    setMeterForm({
      make: kit.make || '',
      model: kit.model || '',
      serial: kit.serial || '',
    });
  }, [settings, orderKind]);

  const meterMutation = useMutation({
    mutationFn: async (kit) =>
      (await api.put(`${apiBase}/buildings/${buildingId}/calibration-kit`, kit)).data?.data,
    onSuccess: async () => {
      await afterBuildingSave('Calibration tool saved');
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to save the reference meter'),
  });

  const settingsMutation = useMutation({
    mutationFn: async (payload) => {
      if (selectedGroup?.id) {
        const assetIds = (Array.isArray(data?.assets) ? data.assets : [])
          .filter((a) => Number(a.schedule_group_id) === Number(selectedGroup.id))
          .map((a) => a.id);
        return (
          await api.put(`${apiBase}/buildings/${buildingId}/schedule-groups/${selectedGroup.id}`, {
            name: groupNameDraft || selectedGroup.name,
            frequency: payload.frequency,
            cycle_start_date: payload.cycle_start_date || null,
            cycle_start_month: payload.cycle_start_month,
            package_plan: payload.package_plan,
            notes: payload.notes,
            asset_ids: assetIds,
            cal_frequency: payload.frequency,
            cal_cycle_start_date: payload.cycle_start_date || null,
            cal_package_plan: payload.package_plan,
          })
        ).data?.data;
      }
      return (await api.put(`${apiBase}/buildings/${buildingId}/settings`, payload)).data?.data;
    },
    onSuccess: async () => {
      setShowSettings(false);
      await afterBuildingSave('Schedule saved');
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to save settings'),
  });

  const createGroupMutation = useMutation({
    mutationFn: async (name) =>
      (
        await api.post(`${apiBase}/buildings/${buildingId}/schedule-groups`, {
          name: name || `Building ${(scheduleGroups?.length || 0) + 1}`,
          frequency: settingsForm.frequency || 'QUARTERLY',
          cycle_start_date: settingsForm.cycle_start_date || null,
          cycle_start_month: settingsForm.cycle_start_month || 1,
          package_plan: buildPackagePlan(
            settingsForm.pack_intervals,
            settingsForm.pack_interval_months
          ),
          level_id: groupLevelDraft || null,
          asset_ids: [],
        })
      ).data?.data,
    onSuccess: async (row) => {
      if (row?.id) setSelectedGroupId(row.id);
      await afterBuildingSave(
        'Group added — chillers and floor plan stay separate until you move them'
      );
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to add group'),
  });

  const saveGroupMetaMutation = useMutation({
    mutationFn: async () =>
      (
        await api.put(`${apiBase}/buildings/${buildingId}/schedule-groups/${selectedGroup.id}`, {
          name: groupNameDraft || selectedGroup.name,
          level_id: groupLevelDraft || null,
        })
      ).data?.data,
    onSuccess: async () => {
      await afterBuildingSave('Group saved');
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to save group'),
  });

  const deleteGroupMutation = useMutation({
    mutationFn: async (groupId) =>
      (await api.delete(`${apiBase}/buildings/${buildingId}/schedule-groups/${groupId}`)).data?.data,
    onSuccess: async () => {
      setSelectedGroupId(null);
      await afterBuildingSave('Group removed — chillers moved to Default');
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to remove group'),
  });

  const acceptDueMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post(
          `${apiBase}/buildings/${buildingId}/schedule-groups/${selectedGroup.id}/accept-due`,
          {
            service_due: acceptDueDraft.service || null,
            calibration_due: acceptDueDraft.calibration || null,
          }
        )
      ).data?.data,
    onSuccess: async () => {
      await afterBuildingSave('Next due accepted');
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to accept due'),
  });

  const assignAssetGroupMutation = useMutation({
    mutationFn: async ({ assetId, schedule_group_id }) =>
      (
        await api.patch(`${apiBase}/buildings/${buildingId}/assets/${assetId}`, {
          schedule_group_id,
        })
      ).data?.data,
    onSuccess: async () => {
      await afterBuildingSave();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to move chiller'),
  });

  const completeVisitMutation = useMutation({
    mutationFn: async (visitId) =>
      (await api.patch(`${apiBase}/buildings/${buildingId}/visits/${visitId}`, { status: 'DONE' }))
        .data?.data,
    onSuccess: async () => {
      await afterBuildingSave('Visit marked done');
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to complete visit'),
  });

  const { data: checklistView, isLoading: checklistLoading } = useQuery({
    queryKey: ['inspection-modules', moduleCode, 'building', buildingId, orderKind, 'checklist'],
    queryFn: async () =>
      (await api.get(`/inspection-modules/${moduleCode}/buildings/${buildingId}`, { params: orderParams }))
        .data?.data,
    enabled: !!buildingId && !graphOpen && !error?.response?.status,
    retry: false,
  });

  const saveChecklist = useMutation({
    mutationFn: async ({ code, body }) =>
      (
        await api.put(
          `/inspection-modules/${moduleCode}/buildings/${buildingId}/sets/${code}`,
          { ...body, order_kind: orderKind },
          { params: orderParams }
        )
      ).data?.data,
    onSuccess: async () => {
      await afterBuildingSave('Checklist saved for this building');
      await queryClient.invalidateQueries({
        queryKey: ['inspection-modules', moduleCode, 'building', buildingId],
      });
      await queryClient.refetchQueries({
        queryKey: ['inspection-modules', moduleCode, 'building', buildingId],
        type: 'active',
      });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to save checklist'),
  });

  const hideChecklist = useMutation({
    mutationFn: async (code) =>
      (await api.delete(`/inspection-modules/${moduleCode}/buildings/${buildingId}/sets/${code}`, {
        params: orderParams,
      })).data,
    onSuccess: async () => {
      await afterBuildingSave();
      await queryClient.invalidateQueries({
        queryKey: ['inspection-modules', moduleCode, 'building', buildingId],
      });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to hide pack'),
  });

  const showChecklistPack = useMutation({
    mutationFn: async (code) =>
      (
        await api.post(
          `/inspection-modules/${moduleCode}/buildings/${buildingId}/sets/${code}/restore`,
          { order_kind: orderKind },
          { params: orderParams }
        )
      ).data,
    onSuccess: async () => {
      await afterBuildingSave('Pack shown');
      await queryClient.invalidateQueries({
        queryKey: ['inspection-modules', moduleCode, 'building', buildingId],
      });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to show pack'),
  });

  const allChillers = useMemo(() => {
    const rows = Array.isArray(data?.assets) ? data.assets : Array.isArray(data?.chillers) ? data.chillers : [];
    return [...rows].sort((a, b) => {
      const rank = (row) => (isActiveAsset(row) ? 0 : 1);
      const byStatus = rank(a) - rank(b);
      if (byStatus) return byStatus;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }, [data]);
  const chillers = useMemo(() => {
    if (!selectedGroup?.id) return allChillers;
    return allChillers.filter((row) => {
      const gid = row.schedule_group_id;
      if (gid != null && gid !== '') {
        return Number(gid) === Number(selectedGroup.id);
      }
      // Unassigned rows only appear on the default group for this building.
      return !!selectedGroup.is_default;
    });
  }, [allChillers, selectedGroup]);

  /** Calibration floor plan: chiller parent + nested door/monitoring sensors. */
  const calibrationFloorGroups = useMemo(() => {
    return chillers.map((asset) => {
      const sensors = sensorsOfAsset(asset).filter((row) => {
        if (asset.no_monitoring_equipment && row.role === 'monitoring') return false;
        return true;
      });
      const codes = sensors.map((row) => row.code || row.letter).join(' ');
      const children = sensors.map((sensor) => {
        const pin =
          sensor.pin_x != null && sensor.pin_y != null
            ? {
                x: Number(sensor.pin_x),
                y: Number(sensor.pin_y),
                level_id: sensor.level_id || asset.level_id || null,
              }
            : null;
        const sensorActive = sensor.enabled !== false;
        const code = sensor.code || sensor.letter;
        return {
          id: `${asset.id}::${sensor.id}`,
          asset_id: asset.id,
          sensor_id: sensor.id,
          name: sensor.label,
          short_label: reportSensorName(asset.name, code),
          // Titles: "Chiller 1 door sensor temperature"; codes: C1-DS1 / C1-MS1
          report_label: sensorTestTitle(asset.name, sensor.role, sensor.seq),
          report_code: reportSensorName(asset.name, code),
          status: sensorActive && isActiveAsset(asset) ? 'ACTIVE' : 'INACTIVE',
          sensor_role: sensor.role,
          sensor_enabled: sensorActive,
          pin_x: pin?.x ?? null,
          pin_y: pin?.y ?? null,
          level_id: pin?.level_id ?? null,
          pin,
          _asset: asset,
        };
      });
      return {
        id: asset.id,
        name: asset.name,
        short_label: asset.name,
        status: asset.status || 'ACTIVE',
        pin_x: asset.pin_x,
        pin_y: asset.pin_y,
        level_id: asset.level_id,
        pin: asset.pin || null,
        sensor_summary: codes,
        children,
        _asset: asset,
      };
    });
  }, [chillers]);

  const floorPlanAssets = chillers;
  const floorPlanGroups = orderKind === 'CALIBRATION' ? calibrationFloorGroups : null;

  async function patchAssetSensors(assetId, nextSensors, extra = {}) {
    await api.patch(`${apiBase}/buildings/${buildingId}/assets/${assetId}`, {
      calibration_sensors: nextSensors,
      ...extra,
    });
    await refreshBuildingModule(queryClient, buildingId, moduleCode);
  }

  async function saveCalibrationFloorPin(item, { pin_x, pin_y, level_id }) {
    // Calibration floor: sensors only — chiller place stays on the Chiller tab.
    if (!item?.sensor_id) {
      throw new Error('On Calibration, place DS/MS sensors only. Chiller place is set on the Chiller tab.');
    }
    const assetId = item.asset_id;
    const asset = allChillers.find((row) => String(row.id) === String(assetId));
    if (!asset) throw new Error('Chiller not found');
    const sensors = defaultCalibrationSensors(asset).map((row) => {
      if (String(row.id) !== String(item.sensor_id)) return row;
      if (pin_x == null || pin_y == null) {
        const next = { ...row };
        delete next.pin_x;
        delete next.pin_y;
        delete next.level_id;
        return next;
      }
      return { ...row, pin_x, pin_y, level_id: level_id || null };
    });
    await patchAssetSensors(assetId, sensors);
  }

  const sensorMutation = useMutation({
    mutationFn: async ({ assetId, sensors }) => patchAssetSensors(assetId, sensors),
    onSuccess: () => toast.success('Sensor updated'),
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to update sensor'),
  });

  function setSensorEnabled(asset, sensorId, enabled) {
    const sensors = defaultCalibrationSensors(asset).map((row) =>
      String(row.id) === String(sensorId) ? { ...row, enabled } : row
    );
    sensorMutation.mutate({ assetId: asset.id, sensors });
  }

  function removeSensor(asset, sensorId) {
    const sensors = defaultCalibrationSensors(asset);
    const target = sensors.find((row) => String(row.id) === String(sensorId));
    if (!target) return;
    if (target.role === 'door') {
      toast.error('Door sensor stays on the unit — disable it instead of removing.');
      return;
    }
    if (!window.confirm(`Remove ${target.label || 'this sensor'} from ${asset.name}?`)) return;
    sensorMutation.mutate({
      assetId: asset.id,
      sensors: sensors.filter((row) => String(row.id) !== String(sensorId)),
    });
  }

  const forbidden = error?.response?.status === 403;

  const { data: levelsSetup } = useQuery({
    queryKey: ['building', buildingId, 'levels-setup', 'chiller-groups'],
    queryFn: async () => (await api.get(`/buildings/${buildingId}/levels-setup`)).data?.data,
    enabled: !!buildingId && !forbidden,
    staleTime: 60_000,
  });
  const placeLevels = useMemo(
    () => (Array.isArray(levelsSetup?.levels) ? levelsSetup.levels : []),
    [levelsSetup]
  );

  const packIntervalCodes = useMemo(() => {
    const fromSets = (checklistView?.checklist_sets || [])
      .map((set) => String(set.code || '').toUpperCase())
      .filter(Boolean);
    const fromForm = Object.keys(settingsForm.pack_intervals || {});
    const codes = [...new Set([...fromSets, ...fromForm, 'A', 'B', 'C'])];
    return codes.sort();
  }, [checklistView, settingsForm.pack_intervals]);

  function openCreate() {
    setEditingId(null);
    setForm({ ...EMPTY_ASSET, calibration_sensors: defaultCalibrationSensors(null) });
    setShowAdd(true);
  }

  function openEdit(asset) {
    setEditingId(asset.id);
    setForm(assetToForm(asset));
    setShowAdd(true);
  }

  function setPackInterval(code, interval) {
    setSettingsForm((f) => {
      const pack_intervals = { ...(f.pack_intervals || {}), [code]: interval };
      const pack_interval_months = { ...(f.pack_interval_months || {}) };
      if (interval === 'EVERY_VISIT') {
        if (!normalizeMonthList(pack_interval_months[code]).length) {
          pack_interval_months[code] = frequencyVisitMonths(f.frequency, f.cycle_start_month);
        }
      } else {
        delete pack_interval_months[code];
      }
      return { ...f, pack_intervals, pack_interval_months };
    });
  }

  function togglePackMonth(code, month) {
    setSettingsForm((f) => {
      const current = normalizeMonthList(f.pack_interval_months?.[code]);
      const next = current.includes(month)
        ? current.filter((m) => m !== month)
        : [...current, month].sort((a, b) => a - b);
      return {
        ...f,
        pack_interval_months: { ...(f.pack_interval_months || {}), [code]: next },
      };
    });
  }

  function saveSchedule() {
    const startDate = settingsForm.cycle_start_date || '';
    const monthFromDate = startDate ? Number(startDate.slice(5, 7)) : settingsForm.cycle_start_month;
    settingsMutation.mutate({
      frequency: settingsForm.frequency,
      cycle_start_month: monthFromDate || 1,
      cycle_start_date: startDate || null,
      notes: settingsForm.notes,
      package_plan: buildPackagePlan(
        settingsForm.pack_intervals,
        settingsForm.pack_interval_months
      ),
      order_kind: orderKind,
    });
  }

  return (
    <div>
      <BuildingPageHeading
        buildingId={buildingId}
        buildingName={data?.building?.name}
        title={graphOpen ? 'Calibration graph' : orderKind === 'CALIBRATION' ? 'Calibration' : deviceLabel}
        infoTitle={graphOpen ? 'Calibration graph' : orderKind === 'CALIBRATION' ? 'Temperature calibration' : deviceLabel}
        tabs={
          moduleCode === 'CHILLER'
            ? [
                { id: 'INSPECTION', label: 'Chiller' },
                { id: 'CALIBRATION', label: 'Calibration' },
                { id: 'GRAPH', label: 'Calibration graph' },
              ]
            : undefined
        }
        activeTab={graphOpen ? 'GRAPH' : orderKind}
        onTabChange={(id) => {
          if (id === 'GRAPH') {
            setGraphOpen(true);
            setShowSettings(false);
            setChecklistAsset(null);
            return;
          }
          setGraphOpen(false);
          setOrderKind(id);
          setShowSettings(false);
          setChecklistAsset(null);
        }}
        infoChildren={
          <div className="space-y-2 text-sm">
            {(orderKind === 'CALIBRATION' || graphOpen) && (
              <p>One meter for this building. Every room reuses it until the meter is replaced.</p>
            )}
            {graphOpen && (
              <p>
                Each bar is this inspection’s display temperature minus the reference thermometer. Compare it
                with a past period, or with the average, highest, or lowest of earlier visits. Pass when the
                bar sits between the limit lines.
              </p>
            )}
            <p>
              Assets, visit packs, and schedule for {deviceLabel}. Separate from BWOF / Specified Systems.
            </p>
            <p>Place this building’s {deviceLabel} assets on the existing floor drawings.</p>
            <p>Select an asset, then click the drawing.</p>
            <p>Add an asset before placing a pin.</p>
          </div>
        }
      />

      {forbidden && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 mb-4">
          {deviceLabel} is not enabled for this company. Turn it on in{' '}
          <Link to="/settings/inspection" className="font-semibold underline">
            Inspection module
          </Link>
          .
        </div>
      )}

      {!forbidden && graphOpen && (
        <CalibrationGraph buildingId={buildingId} moduleCode={moduleCode} assets={chillers} />
      )}

      {!forbidden && !graphOpen && (() => {
        const openVisits = (Array.isArray(visits) ? visits : []).filter(
          (v) => String(v.status || '').toUpperCase() === 'OPEN'
        );
        if (!openVisits.length) return null;
        return (
          <SectionCard title="Open visit" flush ruled={false} className="mb-4">
            <div className="px-4 py-3 space-y-3">
              <p className="text-sm text-amber-950 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Opened — same as BWOF <strong>Accepted</strong>.{' '}
                <strong>Admin next:</strong> assign workers in Visit schedule (they get an Inspection
                card on the phone). Several workers can inspect different chillers at the same time;
                each save updates the shared visit.
              </p>
              <ul className="space-y-2">
                {openVisits.map((v) => (
                  <li
                    key={v.id}
                    className="flex flex-wrap items-center gap-3 rounded-lg border border-green-300 bg-green-100 px-3 py-2 text-sm"
                  >
                    <span className="font-semibold tabular-nums">{formatDate(v.visit_date)}</span>
                    <span className="text-gray-700">{v.period_key || '—'}</span>
                    <span className="text-gray-800">
                      {(v.package_codes || []).join('+') || '—'}
                      {v.includes_calibration ? ' · cal' : ''}
                    </span>
                    <span className="font-medium text-amber-800">OPEN</span>
                    <span className="text-xs text-gray-600">
                      {(v.assignees || []).length
                        ? `Workers: ${(v.assignees || [])
                            .map((a) => a.worker_name || a.worker_id)
                            .join(', ')}`
                        : 'No workers assigned yet'}
                    </span>
                    <a
                      href="#chiller-visit-schedule"
                      className="ml-auto text-xs font-semibold text-primary-700 hover:underline"
                    >
                      Assign / schedule
                    </a>
                    <button
                      type="button"
                      className="text-xs font-semibold text-primary-700 hover:underline"
                      disabled={completeVisitMutation.isPending}
                      onClick={() => completeVisitMutation.mutate(v.id)}
                    >
                      Mark done
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </SectionCard>
        );
      })()}

      {!forbidden && !graphOpen && moduleCode === 'CHILLER' && (
        <SectionCard
          title="Chiller ↔ Calibration units"
          flush
          ruled={false}
          actions={
            <button
              type="button"
              role="switch"
              aria-checked={assetsLinked}
              disabled={assetsLinkMutation.isPending}
              onClick={() => {
                if (assetsLinked) {
                  const ok = window.confirm(
                    'Unlink Chiller and Calibration units?\n\nCurrent units stay on Chiller. Matching copies are created for Calibration so you can edit or delete them separately.'
                  );
                  if (!ok) return;
                  assetsLinkMutation.mutate(false);
                  return;
                }
                const ok = window.confirm(
                  'Link Chiller and Calibration units again?\n\nCalibration copies made while unlinked will be removed. Chiller units become shared again.'
                );
                if (!ok) return;
                assetsLinkMutation.mutate(true);
              }}
              className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border transition-colors disabled:opacity-50 ${
                assetsLinked
                  ? 'border-primary-600 bg-primary-600'
                  : 'border-gray-300 bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                  assetsLinked ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          }
        >
          <div className="px-4 py-3 text-sm text-gray-700 space-y-1">
            <p>
              {assetsLinked ? (
                <>
                  <span className="font-semibold text-primary-800">Linked</span>
                  {' — '}Chiller and Calibration share the same unit list (default). Delete or edit
                  on one tab applies to both.
                </>
              ) : (
                <>
                  <span className="font-semibold text-amber-800">Unlinked</span>
                  {' — '}Each tab has its own units. Changes on Chiller do not affect Calibration.
                </>
              )}
            </p>
            <button
              type="button"
              disabled={assetsLinkMutation.isPending || !assetsLinked}
              onClick={() => {
                const ok = window.confirm(
                  'Unlink Chiller and Calibration units?\n\nCurrent units stay on Chiller. Matching copies are created for Calibration so you can edit or delete them separately.'
                );
                if (!ok) return;
                assetsLinkMutation.mutate(false);
              }}
              className="mt-1 px-3 py-1.5 text-sm font-semibold rounded-lg border border-green-300 bg-green-100 hover:bg-green-200 disabled:opacity-40"
            >
              Unlink units
            </button>
          </div>
        </SectionCard>
      )}

      {!forbidden && !graphOpen && (
        <>
          <SectionCard
            title="Chiller groups"
            flush
            ruled={false}
            actions={
              <button
                type="button"
                onClick={() => {
                  const suggested = `Building ${String.fromCharCode(65 + (scheduleGroups?.length || 0))}`;
                  const name = window.prompt('Group name (e.g. Building B)', suggested);
                  if (name == null) return;
                  const trimmed = String(name).trim();
                  if (!trimmed) return;
                  createGroupMutation.mutate(trimmed);
                }}
                disabled={createGroupMutation.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-green-300 rounded-lg bg-green-100 hover:bg-green-200"
              >
                <Plus className="w-4 h-4" /> Add group
              </button>
            }
          >
            <div className="px-4 py-3 space-y-3">
              <p className="text-xs text-gray-500">
                Name each group after the area (Building B, Building E…). Chillers and that group’s
                floor-plan Place stay together. Old visits are kept.
              </p>
              <div className="flex flex-wrap gap-2">
                {scheduleGroups.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => setSelectedGroupId(g.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm border ${
                      Number(selectedGroup?.id) === Number(g.id)
                        ? 'border-primary-500 bg-primary-50 text-primary-800 font-semibold'
                        : 'border-green-300 bg-green-100 hover:bg-green-200'
                    }`}
                  >
                    {g.name}
                    <span className="ml-1 text-xs text-gray-500">({g.asset_count || 0})</span>
                  </button>
                ))}
                {!scheduleGroups.length && (
                  <p className="text-sm text-gray-500">Loading groups…</p>
                )}
              </div>
              {selectedGroup && (
                <div className="rounded-lg border border-green-200 bg-white/70 p-3 space-y-3">
                  <div className="flex flex-wrap items-end gap-3">
                    <label className="text-sm">
                      Group name
                      <input
                        className="mt-1 block w-48 border rounded px-2 py-1.5"
                        value={groupNameDraft}
                        onChange={(e) => setGroupNameDraft(e.target.value)}
                        placeholder="Building B"
                      />
                    </label>
                    <label className="text-sm">
                      Floor plan Place
                      <select
                        className="mt-1 block min-w-[12rem] border rounded px-2 py-1.5"
                        value={groupLevelDraft}
                        onChange={(e) => setGroupLevelDraft(e.target.value)}
                      >
                        <option value="">—</option>
                        {placeLevels.map((level) => (
                          <option key={level.id} value={level.id}>
                            {[level.block_label, level.label || level.code]
                              .filter(Boolean)
                              .join(' · ') || level.id}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      onClick={() => saveGroupMetaMutation.mutate()}
                      disabled={saveGroupMetaMutation.isPending}
                      className="px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-semibold disabled:opacity-50"
                    >
                      Save name / Place
                    </button>
                    <p className="text-sm text-gray-600">
                      Start{' '}
                      <span className="font-semibold tabular-nums">
                        {formatDisplayDate(selectedGroup.cycle_start_date) || '—'}
                      </span>
                      {' · '}
                      {selectedGroup.frequency || 'QUARTERLY'}
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowSettings(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-green-300 rounded-lg bg-green-100 hover:bg-green-200"
                    >
                      <Settings2 className="w-4 h-4" /> Edit start / packs
                    </button>
                    {!selectedGroup.is_default && (
                      <button
                        type="button"
                        className="text-xs font-semibold text-red-700 hover:underline"
                        disabled={deleteGroupMutation.isPending}
                        onClick={() => {
                          const fallback =
                            scheduleGroups.find((g) => g.is_default)?.name || 'Default';
                          if (
                            !window.confirm(
                              `Remove group “${selectedGroup.name}”? Chillers move to “${fallback}”. Visits stay.`
                            )
                          ) {
                            return;
                          }
                          deleteGroupMutation.mutate(selectedGroup.id);
                        }}
                      >
                        Remove group
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="text-sm">
                      Next inspection due
                      <input
                        type="date"
                        className="mt-1 w-full border rounded px-2 py-1.5 tabular-nums"
                        value={acceptDueDraft.service}
                        onChange={(e) =>
                          setAcceptDueDraft((d) => ({ ...d, service: e.target.value }))
                        }
                      />
                    </label>
                    <label className="text-sm">
                      Next calibration due
                      <input
                        type="date"
                        className="mt-1 w-full border rounded px-2 py-1.5 tabular-nums"
                        value={acceptDueDraft.calibration}
                        onChange={(e) =>
                          setAcceptDueDraft((d) => ({ ...d, calibration: e.target.value }))
                        }
                      />
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => acceptDueMutation.mutate()}
                    disabled={acceptDueMutation.isPending || !selectedGroup?.id}
                    className="px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-semibold disabled:opacity-50"
                  >
                    Accept next due
                  </button>
                </div>
              )}
            </div>
          </SectionCard>

          <SectionCard
            id="chiller-visit-schedule"
            title="Visit schedule"
            flush
            ruled={false}
            actions={
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-green-300 rounded-lg bg-green-100 hover:bg-green-200"
              >
                <Settings2 className="w-4 h-4" /> Edit schedule
              </button>
            }
          >
            {orderKind !== 'CALIBRATION' && (
              <div className="space-y-2 px-4 py-3">
                {(() => {
                  // Same source as Edit schedule: selected group plan (falls back to building settings).
                  const planSrc = selectedGroup || settings;
                  const plan = planSrc?.package_plan;
                  const freq = planSrc?.frequency || settings?.frequency || 'QUARTERLY';
                  const startMonth =
                    Number(planSrc?.cycle_start_month) ||
                    Number(settings?.cycle_start_month) ||
                    1;
                  return (
                    <>
                      <p className="text-xs text-gray-500">
                        {freq} · cycle starts {MONTH_NAMES[startMonth - 1]}
                        {selectedGroup ? ` · ${groupNameDraft || selectedGroup.name}` : ''}
                      </p>
                      {plan?.intervals && (
                        <p className="text-xs text-gray-600">
                          {Object.keys(plan.intervals)
                            .sort()
                            .map((code) => {
                              const id = plan.intervals[code];
                              const label =
                                INTERVAL_OPTIONS.find((option) => option.id === id)?.label || id;
                              const months = normalizeMonthList(plan.interval_months?.[code]);
                              const monthBit =
                                String(id).toUpperCase() === 'EVERY_VISIT' && months.length
                                  ? ` (${months.map((m) => MONTH_NAMES[m - 1]).join(', ')})`
                                  : '';
                              return `${code} ${label}${monthBit}`;
                            })
                            .join(' · ')}
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            <div className="border-t border-green-200/80">
              <h3 className="px-4 py-3 text-base font-bold tracking-tight text-gray-900">Visits</h3>
              {visitsLoading && <p className="px-4 py-6 text-sm text-gray-500">Loading visits…</p>}
              {!visitsLoading && (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className={sectionTableHeadClass}>
                      <tr>
                        <th className="px-4 py-2">Date</th>
                        <th className="px-4 py-2">Period</th>
                        <th className="px-4 py-2">Packs</th>
                        <th className="px-4 py-2">Workers</th>
                        <th className="px-4 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(Array.isArray(visits) ? visits : []).map((v) => {
                        const isOpen = String(v.status || '').toUpperCase() === 'OPEN';
                        const selected = assignDraft[v.id] || [];
                        return (
                          <tr key={v.id}>
                            <td className="px-4 py-2 whitespace-nowrap align-top">
                              <span className="tabular-nums">{formatDate(v.visit_date)}</span>
                              {isOpen && (
                                <button
                                  type="button"
                                  className="ml-3 text-xs font-semibold text-primary-700 hover:underline"
                                  disabled={completeVisitMutation.isPending}
                                  onClick={() => completeVisitMutation.mutate(v.id)}
                                >
                                  Mark done
                                </button>
                              )}
                            </td>
                            <td className="px-4 py-2 align-top">{v.period_key || '—'}</td>
                            <td className="px-4 py-2 align-top">
                              {(v.package_codes || []).join('+') || '—'}
                              {v.includes_calibration ? ' · cal' : ''}
                            </td>
                            <td className="px-4 py-2 align-top min-w-[14rem]">
                              {isOpen ? (
                                <div className="space-y-2">
                                  <div className="max-h-28 overflow-y-auto rounded border border-green-300 bg-white/80 px-2 py-1 space-y-1">
                                    {(Array.isArray(workers) ? workers : []).map((w) => {
                                      const wid = String(w.id);
                                      const checked = selected.includes(wid);
                                      return (
                                        <label
                                          key={wid}
                                          className="flex items-center gap-2 text-xs text-gray-800"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => {
                                              setAssignDraft((prev) => {
                                                const cur = prev[v.id] || [];
                                                const next = checked
                                                  ? cur.filter((id) => id !== wid)
                                                  : [...cur, wid];
                                                return { ...prev, [v.id]: next };
                                              });
                                            }}
                                          />
                                          <span className="truncate">
                                            {w.name || w.username || wid}
                                          </span>
                                        </label>
                                      );
                                    })}
                                    {!workers.length && (
                                      <p className="text-[11px] text-gray-500 py-1">No workers</p>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    className="text-xs font-semibold px-2 py-1 rounded border border-primary-600 text-primary-800 bg-primary-50 disabled:opacity-50"
                                    disabled={assignVisitMutation.isPending}
                                    onClick={() =>
                                      assignVisitMutation.mutate({
                                        visitId: v.id,
                                        workerIds: assignDraft[v.id] || [],
                                      })
                                    }
                                  >
                                    Assign to phone
                                  </button>
                                  {(v.assignees || []).length > 0 && (
                                    <p className="text-[11px] text-gray-600">
                                      On phone:{' '}
                                      {(v.assignees || [])
                                        .map((a) => a.worker_name || a.worker_id)
                                        .join(', ')}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-500">
                                  {(v.assignees || [])
                                    .map((a) => a.worker_name || a.worker_id)
                                    .join(', ') || '—'}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2 align-top">
                              <span
                                className={
                                  isOpen
                                    ? 'text-amber-700 font-medium'
                                    : v.status === 'DONE'
                                      ? 'text-emerald-700'
                                      : 'text-gray-500'
                                }
                              >
                                {v.status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                      {!visits.length && (
                        <tr>
                          <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                            No visits yet. The phone opens a visit when the worker starts, or assign
                            workers after it is OPEN.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </SectionCard>

          {orderKind === 'CALIBRATION' && (
            <SectionCard
              title="Calibration tool"
              actions={
                <button
                  type="button"
                  disabled={meterMutation.isPending}
                  onClick={() => meterMutation.mutate(meterForm)}
                  className="px-3 py-1.5 text-sm rounded-lg bg-slate-800 text-white font-semibold disabled:opacity-50"
                >
                  {meterMutation.isPending ? 'Saving…' : 'Save'}
                </button>
              }
            >
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="text-sm">
                  Company
                  <input
                    className="mt-1 block w-full border rounded px-2 py-1.5"
                    value={meterForm.make}
                    onChange={(e) => setMeterForm((prev) => ({ ...prev, make: e.target.value }))}
                  />
                </label>
                <label className="text-sm">
                  Model
                  <input
                    className="mt-1 block w-full border rounded px-2 py-1.5"
                    value={meterForm.model}
                    onChange={(e) => setMeterForm((prev) => ({ ...prev, model: e.target.value }))}
                  />
                </label>
                <label className="text-sm">
                  Serial
                  <input
                    className="mt-1 block w-full border rounded px-2 py-1.5"
                    value={meterForm.serial}
                    onChange={(e) => setMeterForm((prev) => ({ ...prev, serial: e.target.value }))}
                  />
                </label>
              </div>
            </SectionCard>
          )}

          <SectionCard
            title="Setting Check List"
            actions={
              <>
              <label className="text-sm">
                New pack code
                <input
                  className="mt-1 block border border-green-300 rounded px-2 py-1.5 w-24"
                  value={packCode}
                  onChange={(e) => setPackCode(e.target.value.toUpperCase())}
                />
              </label>
              <button
                type="button"
                className="px-3 py-1.5 text-sm border border-green-300 rounded bg-green-100"
                onClick={() =>
                  saveChecklist.mutate({
                    code: packCode,
                    body: { code: packCode, name: `Pack ${packCode}`, items: [] },
                  })
                }
              >
                Add pack
              </button>
              </>
            }
          >
            <p className="text-xs text-gray-500">
              Pack A is the sealed fridge or freezer and is on every visit. Pack P is the piped plant and cool room. Packs P, B, and C stay off until Visit schedule turns them on. Edit removes a question from this building. Save stores this building only.
            </p>
            {checklistLoading && <p className="text-sm text-gray-500">Loading checklist…</p>}
            {(checklistView?.checklist_sets || []).map((set) => (
              <InspectionQuestionEditor
                key={`${set.code}-${set.updated_at || ''}`}
                pack={set}
                embedded
                saving={saveChecklist.isPending && saveChecklist.variables?.code === set.code}
                onSave={(code, body) => saveChecklist.mutate({ code, body })}
                onDelete={(code) => hideChecklist.mutate(code)}
              />
            ))}
            {(checklistView?.hidden_checklist_sets || []).map((set) => (
              <div
                key={`hidden-${set.code}`}
                className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-green-400 px-4 py-3 text-sm"
              >
                <span className="font-semibold">{set.code}</span>
                <button
                  type="button"
                  className="px-3 py-1.5 rounded bg-primary-600 text-white text-sm font-semibold disabled:opacity-50"
                  disabled={showChecklistPack.isPending}
                  onClick={() => showChecklistPack.mutate(set.code)}
                >
                  Show
                </button>
                <span className="text-gray-500">{set.name || 'Pack'} · hidden</span>
              </div>
            ))}
          </SectionCard>
        </>
      )}

      {!graphOpen && (
      <SectionCard
        title={
          selectedGroup?.name
            ? `Registered ${orderKind === 'CALIBRATION' ? 'Calibration' : 'Chiller'} · ${
                groupNameDraft || selectedGroup.name
              }`
            : orderKind === 'CALIBRATION'
              ? 'Registered Calibration units'
              : 'Registered Chiller'
        }
        flush
        actions={
          !forbidden ? (
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold"
            >
              <Plus className="w-4 h-4" />{' '}
              {orderKind === 'CALIBRATION' ? 'Add calibration unit' : 'Add chiller'}
            </button>
          ) : null
        }
      >
        {isLoading && <p className="px-4 py-8 text-gray-500 text-sm">Loading assets…</p>}
        {isError && !forbidden && (
          <p className="px-4 py-8 text-red-600 text-sm">
            {error?.response?.data?.error || `Failed to load ${deviceLabel}`}
          </p>
        )}
        {!isLoading && !isError && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className={sectionTableHeadClass}>
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Tag / serial</th>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Group</th>
                  <th className="px-4 py-3">
                    {orderKind === 'CALIBRATION' ? 'Last calibration' : 'Last inspection'}
                  </th>
                  <th className="px-4 py-3">
                    {orderKind === 'CALIBRATION' ? 'Next calibration' : 'Next inspection'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {chillers.map((c) => {
                  const active = isActiveAsset(c);
                  const sensors =
                    orderKind === 'CALIBRATION'
                      ? sensorsOfAsset(c).filter((row) => {
                          if (c.no_monitoring_equipment && row.role === 'monitoring') return false;
                          return true;
                        })
                      : [];
                  return (
                    <Fragment key={c.id}>
                      <tr className={active ? '' : 'text-gray-400'}>
                        <td className="px-4 py-3">
                          <p className={`font-semibold ${active ? 'text-gray-900' : 'text-gray-400'}`}>
                            {c.name}
                          </p>
                          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                            <button
                              type="button"
                              className="text-xs font-semibold text-primary-700 hover:underline"
                              onClick={() => setChecklistAsset(c)}
                            >
                              Checklist
                            </button>
                            <button
                              type="button"
                              className="text-xs font-semibold text-primary-700 hover:underline"
                              onClick={() => openEdit(c)}
                            >
                              Edit
                            </button>
                            {active ? (
                              <button
                                type="button"
                                className="text-xs font-semibold text-red-600 hover:underline"
                                disabled={statusMutation.isPending}
                                onClick={() => statusMutation.mutate({ id: c.id, status: 'INACTIVE' })}
                              >
                                Disable
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="text-xs font-semibold text-emerald-700 hover:underline"
                                disabled={statusMutation.isPending}
                                onClick={() => statusMutation.mutate({ id: c.id, status: 'ACTIVE' })}
                              >
                                Enable
                              </button>
                            )}
                            <button
                              type="button"
                              className="text-xs font-semibold text-red-700 hover:underline"
                              disabled={statusMutation.isPending}
                              onClick={() => {
                                const name = c.name || 'this chiller';
                                if (!window.confirm(`Remove ${name}? It leaves this building and the phone.`))
                                  return;
                                statusMutation.mutate({ id: c.id, status: 'PURGED' });
                              }}
                            >
                              Remove
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {[c.manufacturer, c.model].filter(Boolean).join(' · ') || '—'}
                            {c.electrical_phase ? ` · ${c.electrical_phase}φ` : ''}
                            {!active ? ' · Disabled' : ''}
                            {orderKind === 'CALIBRATION' && sensors.length
                              ? ` · Sensors ${sensors.map((s) => s.code || s.letter).join(' ')}`
                              : ''}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-xs font-medium">{c.asset_type || 'CHILLER'}</td>
                        <td className="px-4 py-3">{c.asset_tag || c.serial_number || '—'}</td>
                        <td className="px-4 py-3">{c.location_label || '—'}</td>
                        <td className="px-4 py-3">
                          <select
                            className="border rounded px-1.5 py-1 text-xs max-w-[9rem]"
                            value={c.schedule_group_id || ''}
                            disabled={assignAssetGroupMutation.isPending}
                            onChange={(e) =>
                              assignAssetGroupMutation.mutate({
                                assetId: c.id,
                                schedule_group_id: e.target.value ? Number(e.target.value) : null,
                              })
                            }
                          >
                            <option value="">—</option>
                            {scheduleGroups.map((g) => (
                              <option key={g.id} value={g.id}>
                                {g.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3 tabular-nums">
                          {formatDisplayDate(
                            orderKind === 'CALIBRATION' ? c.last_calibration_at : c.last_service_at
                          )}
                        </td>
                        <td className="px-4 py-3 tabular-nums">
                          {formatDisplayDate(
                            orderKind === 'CALIBRATION' ? c.next_calibration_due : c.next_service_due
                          )}
                        </td>
                      </tr>
                      {orderKind === 'CALIBRATION' &&
                        sensors.map((sensor) => {
                          const sensorOn = sensor.enabled !== false;
                          return (
                            <tr
                              key={`${c.id}-${sensor.id}`}
                              className={sensorOn && active ? 'bg-green-50/40' : 'bg-gray-50 text-gray-400'}
                            >
                              <td className="px-4 py-2 pl-10" colSpan={1}>
                                <p className="font-medium text-gray-900">
                                  <span className="tabular-nums font-bold mr-1">
                                    {sensor.code || sensor.letter}
                                  </span>
                                  {sensor.label}
                                  <span className="ml-2 text-xs font-normal text-gray-500">
                                    {sensor.role === 'door' ? 'Door sensor' : 'Monitoring sensor'}
                                    {!sensorOn ? ' · Off' : ''}
                                  </span>
                                </p>
                                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                                  <button
                                    type="button"
                                    className="text-xs font-semibold text-primary-700 hover:underline"
                                    onClick={() => setChecklistAsset(c)}
                                  >
                                    Checklist
                                  </button>
                                  <button
                                    type="button"
                                    className="text-xs font-semibold text-primary-700 hover:underline"
                                    onClick={() => openEdit(c)}
                                  >
                                    Edit
                                  </button>
                                  {sensorOn ? (
                                    <button
                                      type="button"
                                      className="text-xs font-semibold text-red-600 hover:underline"
                                      disabled={sensorMutation.isPending}
                                      onClick={() => setSensorEnabled(c, sensor.id, false)}
                                    >
                                      Disable
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      className="text-xs font-semibold text-emerald-700 hover:underline"
                                      disabled={sensorMutation.isPending}
                                      onClick={() => setSensorEnabled(c, sensor.id, true)}
                                    >
                                      Enable
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="text-xs font-semibold text-red-700 hover:underline"
                                    disabled={sensorMutation.isPending}
                                    onClick={() => removeSensor(c, sensor.id)}
                                  >
                                    Remove
                                  </button>
                                </div>
                              </td>
                              <td className="px-4 py-2 text-xs">
                                {sensor.role === 'door' ? 'DOOR' : 'MONITOR'}
                              </td>
                              <td className="px-4 py-2 text-xs">{sensor.sensor || '—'}</td>
                              <td className="px-4 py-2 text-xs" colSpan={1}>
                                {sensor.model || '—'}
                              </td>
                              <td className="px-4 py-2 text-xs text-gray-500">—</td>
                              <td className="px-4 py-2 text-xs text-gray-500">—</td>
                              <td className="px-4 py-2 text-xs text-gray-500">
                                Pass ±{sensor.pass_c || '—'}°C
                              </td>
                            </tr>
                          );
                        })}
                    </Fragment>
                  );
                })}
                {!chillers.length && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                      <Snowflake className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      {selectedGroup?.name
                        ? `No chillers in “${groupNameDraft || selectedGroup.name}” yet. Add one or move a chiller into this group.`
                        : 'No plant assets on this building yet.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
      )}

      {!forbidden && !graphOpen && (
          <InspectionFloorPlan
            buildingId={buildingId}
            moduleCode={moduleCode}
            assets={floorPlanAssets}
            groups={floorPlanGroups}
            preferredLevelId={groupLevelDraft || selectedGroup?.level_id || ''}
            groupLabel={groupNameDraft || selectedGroup?.name || ''}
            lockPreferredLevel
            placeChildrenOnly={orderKind === 'CALIBRATION'}
            emptyLabel={
              orderKind === 'CALIBRATION'
                ? 'No chillers yet. Add a unit on Chiller, then place DS/MS sensors here.'
                : 'No assets yet.'
            }
            helpText={
              orderKind === 'CALIBRATION'
                ? 'Chiller place is locked (set on the Chiller tab). Select a sensor (DS1, MS1…), then click the drawing to place it.'
                : 'Select a chiller, then click the drawing to place the unit.'
            }
            onSavePin={orderKind === 'CALIBRATION' ? saveCalibrationFloorPin : undefined}
            onEdit={(asset) => openEdit(asset)}
            onSetEnabled={(asset, enabled, row) => {
              if (orderKind === 'CALIBRATION' && row?.sensor_id) {
                setSensorEnabled(asset, row.sensor_id, enabled);
                return;
              }
              statusMutation.mutate({ id: asset.id, status: enabled ? 'ACTIVE' : 'INACTIVE' });
            }}
            onRemove={(asset, row) => {
              if (orderKind === 'CALIBRATION' && row?.sensor_id) {
                removeSensor(asset, row.sensor_id);
                return;
              }
              const name = asset?.name || 'this chiller';
              if (!window.confirm(`Remove ${name}? It leaves this building and the phone.`)) return;
              statusMutation.mutate({ id: asset.id, status: 'PURGED' });
            }}
            onSaved={() => refreshBuildingModule(queryClient, buildingId, moduleCode)}
            onOpenChecklist={setChecklistAsset}
          />
      )}

      {checklistAsset && (
        <InspectionAssetChecklist
          buildingId={buildingId}
          moduleCode={moduleCode}
          asset={checklistAsset}
          onClose={() => setChecklistAsset(null)}
          onSaved={async (row) => {
            await refreshBuildingModule(queryClient, buildingId, moduleCode);
            if (row && String(row.id) === String(checklistAsset.id)) setChecklistAsset(row);
          }}
          orderKind={orderKind}
        />
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-card rounded-lg shadow-lg w-full max-w-md p-4 space-y-3 max-h-[90vh] overflow-y-auto">
            <h4 className="font-semibold">{editingId ? 'Edit plant asset' : 'Add plant asset'}</h4>
            <label className="block text-sm">
              Name *
              <input
                className="mt-1 w-full border rounded px-2 py-1.5"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-sm">
                Type
                <select
                  className="mt-1 w-full border rounded px-2 py-1.5"
                  value={form.asset_type}
                  onChange={(e) => setForm((f) => ({ ...f, asset_type: e.target.value }))}
                >
                  {ASSET_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                Phase
                <select
                  className="mt-1 w-full border rounded px-2 py-1.5"
                  value={form.electrical_phase}
                  onChange={(e) => setForm((f) => ({ ...f, electrical_phase: e.target.value }))}
                >
                  <option value="">—</option>
                  <option value="1">1φ</option>
                  <option value="3">3φ</option>
                </select>
              </label>
            </div>
            {[
              ['asset_tag', 'Asset tag'],
              ['manufacturer', 'Manufacturer'],
              ['model', 'Model'],
              ['serial_number', 'Serial'],
              ['location_label', 'Location'],
            ].map(([key, label]) => (
              <label key={key} className="block text-sm">
                {label}
                <input
                  className="mt-1 w-full border rounded px-2 py-1.5"
                  value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                />
              </label>
            ))}
            <div className="grid grid-cols-2 gap-2">
              <div className="text-sm">
                Last inspection
                <p className="mt-1 w-full rounded border bg-slate-50 px-2 py-1.5 tabular-nums text-gray-700">
                  {formatDisplayDate(form.last_service_at)}
                </p>
              </div>
              <div className="text-sm">
                Next inspection
                <p className="mt-1 w-full rounded border bg-slate-50 px-2 py-1.5 tabular-nums text-gray-700">
                  {formatDisplayDate(form.next_service_due)}
                </p>
              </div>
              <div className="text-sm">
                Last calibration
                <p className="mt-1 w-full rounded border bg-slate-50 px-2 py-1.5 tabular-nums text-gray-700">
                  {formatDisplayDate(form.last_calibration_at)}
                </p>
              </div>
              <div className="text-sm">
                Next calibration
                <p className="mt-1 w-full rounded border bg-slate-50 px-2 py-1.5 tabular-nums text-gray-700">
                  {formatDisplayDate(form.next_calibration_due)}
                </p>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Filled from completed visits and the visit schedule. After a period is done, next moves
              forward.
            </p>
            <div className="rounded-lg border border-slate-200 p-3 space-y-3">
              <p className="text-sm font-semibold">Calibration sensors</p>
              <p className="text-xs text-slate-500">
                Calibration compares each display with the Calibration Tool at that probe. Enable
                the door sensor, monitoring sensors, or both — each gets its own pass/fail on the
                phone (codes DS1, MS1… → C1-DS1, C1-MS1).
              </p>
              <label className="flex items-start gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={!!form.no_monitoring_equipment}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setForm((current) => ({
                      ...current,
                      no_monitoring_equipment: checked,
                      calibration_sensors: defaultCalibrationSensors(current).map((row) =>
                        row.role === 'monitoring' ? { ...row, enabled: !checked } : row
                      ),
                    }));
                  }}
                />
                <span>
                  No monitoring equipment
                  <span className="block text-xs text-slate-500">
                    Hides monitoring sensors. Door sensor can still be calibrated alone.
                  </span>
                </span>
              </label>

              {(() => {
                const sensors = defaultCalibrationSensors(form);
                const visible = sensors.filter(
                  (row) => !(form.no_monitoring_equipment && row.role === 'monitoring')
                );
                const monitoringCount = sensors.filter((row) => row.role === 'monitoring').length;
                const updateRow = (id, patch) =>
                  setForm((current) => ({
                    ...current,
                    calibration_sensors: defaultCalibrationSensors(current).map((item) =>
                      item.id === id ? { ...item, ...patch } : item
                    ),
                  }));
                return (
                  <>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pt-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Door sensor
                      </p>
                    </div>
                    {visible
                      .filter((row) => row.role === 'door')
                      .map((row) => {
                        return (
                          <div
                            key={row.id}
                            className="rounded-lg border border-slate-200 bg-white p-3 space-y-2"
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <p className="text-sm font-bold text-gray-900">
                                {row.code} · Door sensor
                              </p>
                              <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                                <input
                                  type="checkbox"
                                  checked={row.enabled !== false}
                                  onChange={(e) => updateRow(row.id, { enabled: e.target.checked })}
                                />
                                In use
                              </label>
                            </div>
                            <label className="block text-sm">
                              Name
                              <input
                                className="mt-1 w-full border rounded px-2 py-1.5"
                                value={row.label || ''}
                                onChange={(e) => updateRow(row.id, { label: e.target.value })}
                              />
                            </label>
                            <label className="block text-sm">
                              Model
                              <input
                                className="mt-1 w-full border rounded px-2 py-1.5"
                                value={row.model || ''}
                                onChange={(e) => updateRow(row.id, { model: e.target.value })}
                              />
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              <label className="block text-sm">
                                Sensor
                                <select
                                  className="mt-1 w-full border rounded px-2 py-1.5"
                                  value={row.sensor || 'NTC'}
                                  onChange={(e) => {
                                    const sensor = e.target.value;
                                    updateRow(row.id, { sensor, pass_c: sensorLimit(sensor) });
                                  }}
                                >
                                  {SENSOR_TYPES.map((item) => (
                                    <option key={item.value} value={item.value}>
                                      {item.label}
                                    </option>
                                  ))}
                                </select>
                              </label>
                              <label className="block text-sm">
                                Pass limit (°C)
                                <input
                                  className="mt-1 w-full border rounded px-2 py-1.5"
                                  inputMode="decimal"
                                  value={row.pass_c || ''}
                                  onChange={(e) => updateRow(row.id, { pass_c: e.target.value })}
                                />
                              </label>
                            </div>
                          </div>
                        );
                      })}

                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pt-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Monitoring sensors ({form.no_monitoring_equipment ? 0 : monitoringCount})
                      </p>
                      {!form.no_monitoring_equipment && (
                        <button
                          type="button"
                          className="px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-semibold"
                          onClick={() =>
                            setForm((current) => {
                              const rows = defaultCalibrationSensors(current);
                              return {
                                ...current,
                                calibration_sensors: [...rows, newMonitoringSensor(rows)],
                              };
                            })
                          }
                        >
                          Add monitoring sensor
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">
                      Use one card per probe or logger channel (zone A, zone B, far wall…). Common for
                      wide warehouses on a single walk-in chiller.
                    </p>
                    {visible
                      .filter((row) => row.role === 'monitoring')
                      .map((row) => {
                        const canRemove =
                          sensors.filter((item) => item.role === 'monitoring').length > 1;
                        return (
                          <div
                            key={row.id}
                            className="rounded-lg border border-slate-200 bg-white p-3 space-y-2"
                          >
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                              <p className="text-sm font-bold text-gray-900">
                                {row.code} · Monitoring sensor
                              </p>
                              <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                                <input
                                  type="checkbox"
                                  checked={row.enabled !== false}
                                  onChange={(e) => updateRow(row.id, { enabled: e.target.checked })}
                                />
                                In use
                              </label>
                              {canRemove && (
                                <button
                                  type="button"
                                  className="text-xs font-semibold text-red-600 hover:underline"
                                  onClick={() =>
                                    setForm((current) => ({
                                      ...current,
                                      calibration_sensors: defaultCalibrationSensors(current).filter(
                                        (item) => item.id !== row.id
                                      ),
                                    }))
                                  }
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                            <label className="block text-sm">
                              Location / name
                              <input
                                className="mt-1 w-full border rounded px-2 py-1.5"
                                placeholder="e.g. Zone B · far wall"
                                value={row.label || ''}
                                onChange={(e) => updateRow(row.id, { label: e.target.value })}
                              />
                            </label>
                            <label className="block text-sm">
                              Model
                              <input
                                className="mt-1 w-full border rounded px-2 py-1.5"
                                value={row.model || ''}
                                onChange={(e) => updateRow(row.id, { model: e.target.value })}
                              />
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              <label className="block text-sm">
                                Sensor
                                <select
                                  className="mt-1 w-full border rounded px-2 py-1.5"
                                  value={row.sensor || 'PT100'}
                                  onChange={(e) => {
                                    const sensor = e.target.value;
                                    updateRow(row.id, { sensor, pass_c: sensorLimit(sensor) });
                                  }}
                                >
                                  {SENSOR_TYPES.map((item) => (
                                    <option key={item.value} value={item.value}>
                                      {item.label}
                                    </option>
                                  ))}
                                </select>
                              </label>
                              <label className="block text-sm">
                                Pass limit (°C)
                                <input
                                  className="mt-1 w-full border rounded px-2 py-1.5"
                                  inputMode="decimal"
                                  value={row.pass_c || ''}
                                  onChange={(e) => updateRow(row.id, { pass_c: e.target.value })}
                                />
                              </label>
                            </div>
                          </div>
                        );
                      })}
                  </>
                );
              })()}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="px-3 py-1.5 text-sm border rounded"
                onClick={() => {
                  setShowAdd(false);
                  setEditingId(null);
                  setForm({ ...EMPTY_ASSET, calibration_sensors: defaultCalibrationSensors(null) });
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded bg-primary-600 text-white disabled:opacity-50"
                disabled={!form.name.trim() || saveAsset.isPending}
                onClick={() => saveAsset.mutate(form)}
              >
                {saveAsset.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-card rounded-lg shadow-lg w-full max-w-lg p-4 space-y-3 max-h-[90vh] overflow-y-auto">
            <h4 className="font-semibold">Visit schedule</h4>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-sm">
                Frequency
                <select
                  className="mt-1 w-full border rounded px-2 py-1.5"
                  value={settingsForm.frequency}
                  onChange={(e) =>
                    setSettingsForm((f) => ({ ...f, frequency: e.target.value }))
                  }
                >
                  <option value="QUARTERLY">Quarterly</option>
                  <option value="HALF_YEARLY">Half-yearly</option>
                  <option value="YEARLY">Yearly</option>
                </select>
              </label>
              <label className="block text-sm">
                Cycle start date
                <input
                  type="date"
                  className="mt-1 w-full border rounded px-2 py-1.5 tabular-nums"
                  value={settingsForm.cycle_start_date || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSettingsForm((f) => ({
                      ...f,
                      cycle_start_date: value,
                      cycle_start_month: value
                        ? Number(value.slice(5, 7))
                        : f.cycle_start_month,
                    }));
                  }}
                />
              </label>
              <label className="block text-sm col-span-2">
                Cycle start month (legacy mirror)
                <select
                  className="mt-1 w-full border rounded px-2 py-1.5"
                  value={settingsForm.cycle_start_month}
                  onChange={(e) =>
                    setSettingsForm((f) => ({
                      ...f,
                      cycle_start_month: Number(e.target.value),
                    }))
                  }
                >
                  {MONTH_NAMES.map((name, index) => (
                    <option key={name} value={index + 1}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {selectedGroup && (
              <p className="text-xs text-gray-500">
                Saving updates group “{groupNameDraft || selectedGroup.name}”. This schedule is the
                criterion for open visits and due dates; completed visits keep what was done.
              </p>
            )}
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide text-gray-500">Pack interval</p>
              <p className="text-xs text-gray-500">
                Each pack is a question group. Frequency decides the visit cycle. For Every visit,
                pick any months of the year when that pack should appear.
              </p>
              {packIntervalCodes.map((code) => {
                const interval = settingsForm.pack_intervals?.[code] || (code === 'A' ? 'EVERY_VISIT' : 'OFF');
                const customMonths = settingsForm.pack_interval_months?.[code];
                const months = monthsForInterval(
                  interval,
                  settingsForm.frequency,
                  settingsForm.cycle_start_month,
                  customMonths
                );
                const selectedMonths = normalizeMonthList(customMonths);
                return (
                  <div key={code} className="rounded border px-3 py-2 text-sm space-y-2">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="w-8 font-semibold">{code}</span>
                      <select
                        className="border rounded px-2 py-1.5"
                        value={interval}
                        onChange={(e) => setPackInterval(code, e.target.value)}
                      >
                        {INTERVAL_OPTIONS.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {interval !== 'EVERY_VISIT' && (
                        <span className="text-xs text-gray-500">
                          {months.length
                            ? months.map((monthIndex) => MONTH_NAMES[monthIndex]).join(', ')
                            : 'Not included'}
                        </span>
                      )}
                      {interval === 'EVERY_VISIT' && !selectedMonths.length && (
                        <span className="text-xs text-amber-700">Select at least one month</span>
                      )}
                    </div>
                    {interval === 'EVERY_VISIT' && (
                      <div className="flex flex-wrap gap-1 pl-11">
                        {MONTH_NAMES.map((name, index) => {
                          const month = index + 1;
                          const on = selectedMonths.includes(month);
                          return (
                            <button
                              key={name}
                              type="button"
                              className={`min-w-[2.25rem] rounded border px-1.5 py-1 text-xs ${
                                on
                                  ? 'border-primary-600 bg-primary-50 text-primary-800'
                                  : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                              }`}
                              onClick={() => togglePackMonth(code, month)}
                              aria-pressed={on}
                            >
                              {name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <label className="block text-sm">
              Notes
              <textarea
                className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
                rows={2}
                value={settingsForm.notes}
                onChange={(e) => setSettingsForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="px-3 py-1.5 text-sm border rounded"
                onClick={() => setShowSettings(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded bg-primary-600 text-white disabled:opacity-50"
                disabled={settingsMutation.isPending}
                onClick={saveSchedule}
              >
                {settingsMutation.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
